import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import useTranslation from "../hooks/useTranslation";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import NavbarFooter from "../components/Footer";

export const Route = createFileRoute("/free_trial_signup")({
  component: SignupTwoColumn,
});

function SignupTwoColumn() {
  const { translate } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [username, setUsername] = useState("");

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 md:py-16 grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8">
        {/* Form Section - Shows first on mobile, second on desktop */}
        <section className="order-1 md:order-2 md:col-span-2 bg-white border border-neutral-200 rounded-2xl p-6 md:p-8 shadow-md">
          <h2 className="text-xl md:text-2xl font-semibold text-left">
            {translate("signup.form_title")}
          </h2>
          <p className="text-sm font-light mb-8 text-left">
            {translate("signup.form_sub_title")}
          </p>
          <form className="space-y-4 text-left">
            <div>
              <label className="block mb-1 text-sm font-medium">
                {translate("signup.username")}
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={translate("signup.username_placeholder")}
                className="w-full border rounded-lg px-4 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block mb-1 text-sm font-medium">
                {translate("signup.email")}
              </label>
              <input
                type="email"
                placeholder={translate("signup.email_placeholder")}
                className="w-full border rounded-lg p-2"
                required
              />
            </div>

            <div className="relative">
              <label className="block mb-1 text-sm font-medium">
                {translate("signup.password")}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder={translate("signup.password_placeholder")}
                  className="w-full border rounded-lg p-2 pr-10"
                  required
                />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowPassword(!showPassword)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && setShowPassword(!showPassword)
                  }
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 focus:outline-hidden cursor-pointer"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </span>
              </div>
            </div>

            <div className="relative">
              <label className="block mb-1 text-sm font-medium">
                {translate("signup.repeat_password")}
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={translate("signup.repeat_password_placeholder")}
                  className="w-full border rounded-lg p-2 pr-10"
                  required
                />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    setShowConfirmPassword(!showConfirmPassword)
                  }
                  className="absolute inset-y-0 right-3 flex items-center text-gray-500 focus:outline-hidden ursor-pointer"
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </span>
              </div>
            </div>

            <p className="text-xs text-neutral-500">
              {translate("signup.progress_updates")}
            </p>
            <p className="text-xs text-neutral-500">
              {translate("signup.terms_privacy")}
            </p>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700"
            >
              {translate("signup.register")}
            </button>

            <p className="text-center text-sm text-neutral-500">
              {translate("signup.already_have_account")}
            </p>
          </form>
        </section>

        {/* Features Section - Shows second on mobile, first on desktop */}
        <section className="order-2 md:order-1 md:col-span-3 bg-white py-6 px-2 md:p-8">
          <h1 className="text-2xl md:text-4xl font-semibold mb-6 md:mb-8 text-left">
            {translate("signup.title")}
          </h1>
          <p className="text-left text-lg pb-10">
            {translate("signup.trial_features.intro")}
          </p>
          <h2 className="text-xl md:text-2xl font-semibold mb-2 text-left">
            {translate("signup.trial_includes")}
          </h2>
          <ul className="text-left text-neutral-700 space-y-2">
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
              {translate("signup.trial_features.custom_texts")}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
              {translate("signup.trial_features.translated_terms")}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
              {translate("signup.trial_features.audio_texts")}
            </li>
            <li className="flex items-center gap-2">
              <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
              {translate("signup.trial_features.audio_pronunciation")}
            </li>
          </ul>
          <ul className="list-disc text-left list-inside text-neutral-700 space-y-2 mt-5">
            <li>{translate("signup.benefits.change_anytime")}</li>
            <li>{translate("signup.benefits.access_topics")}</li>
            <li>{translate("signup.benefits.no_payment")}</li>
            <li>{translate("signup.benefits.stop_anytime")}</li>
          </ul>
          <p className="mt-6 text-sm font-bold text-black text-left">
            {translate("signup.getting_started")}
          </p>
        </section>
      </div>
      <div className="pt-28">
        <NavbarFooter />
      </div>
    </>
  );
}
